import * as THREE from 'three';
import { AU } from '../config';
import type { BodyDef, KeplerElements } from './bodies';

// Frame: heliocentric ecliptic J2000, metres. x → vernal equinox, z → ecliptic north.
// All math runs in JS doubles; only camera-relative results reach the GPU.

const DEG = Math.PI / 180;
const OBLIQUITY = 23.43928 * DEG;
const EARTH_MOON_MASS_RATIO = 0.0121505856; // Mmoon / (Mearth + Mmoon)

export function julianDate(ms: number): number {
  return ms / 86_400_000 + 2_440_587.5;
}

export function daysSinceJ2000(ms: number): number {
  return julianDate(ms) - 2_451_545.0;
}

function solveKepler(M: number, e: number): number {
  let E = M + e * Math.sin(M);
  for (let i = 0; i < 8; i++) {
    const dE = (E - e * Math.sin(E) - M) / (1 - e * Math.cos(E));
    E -= dE;
    if (Math.abs(dE) < 1e-12) break;
  }
  return E;
}

/** Heliocentric ecliptic position (m) from JPL Keplerian elements. */
export function keplerPosition(el: KeplerElements, T: number, out: THREE.Vector3): THREE.Vector3 {
  const a = (el.a[0] + el.a[1] * T) * AU;
  const e = el.e[0] + el.e[1] * T;
  const I = (el.I[0] + el.I[1] * T) * DEG;
  const L = (el.L[0] + el.L[1] * T) * DEG;
  const varpi = (el.varpi[0] + el.varpi[1] * T) * DEG;
  const node = (el.node[0] + el.node[1] * T) * DEG;
  const omega = varpi - node;
  let M = (L - varpi) % (2 * Math.PI);
  if (M > Math.PI) M -= 2 * Math.PI;
  if (M < -Math.PI) M += 2 * Math.PI;
  const E = solveKepler(M, e);
  const xp = a * (Math.cos(E) - e);
  const yp = a * Math.sqrt(1 - e * e) * Math.sin(E);
  const cw = Math.cos(omega), sw = Math.sin(omega);
  const cO = Math.cos(node), sO = Math.sin(node);
  const cI = Math.cos(I), sI = Math.sin(I);
  return out.set(
    (cw * cO - sw * sO * cI) * xp + (-sw * cO - cw * sO * cI) * yp,
    (cw * sO + sw * cO * cI) * xp + (-sw * sO + cw * cO * cI) * yp,
    sw * sI * xp + cw * sI * yp,
  );
}

/** Geocentric ecliptic Moon position (m). Astronomical Almanac low-precision series (~0.3°). */
export function lunarPosition(T: number, out: THREE.Vector3): THREE.Vector3 {
  const s = (d: number) => Math.sin(d * DEG);
  const c = (d: number) => Math.cos(d * DEG);
  const lambda =
    218.32 + 481267.881 * T +
    6.29 * s(135.0 + 477198.87 * T) - 1.27 * s(259.3 - 413335.36 * T) +
    0.66 * s(235.7 + 890534.22 * T) + 0.21 * s(269.9 + 954397.74 * T) -
    0.19 * s(357.5 + 35999.05 * T) - 0.11 * s(186.5 + 966404.03 * T);
  const beta =
    5.13 * s(93.3 + 483202.02 * T) + 0.28 * s(228.2 + 960400.89 * T) -
    0.28 * s(318.3 + 6003.15 * T) - 0.17 * s(217.6 - 407332.21 * T);
  const parallax =
    0.9508 + 0.0518 * c(135.0 + 477198.87 * T) + 0.0095 * c(259.3 - 413335.36 * T) +
    0.0078 * c(235.7 + 890534.22 * T) + 0.0028 * c(269.9 + 954397.74 * T);
  const r = 6_378_137 / Math.sin(parallax * DEG);
  return out.set(r * c(beta) * c(lambda), r * c(beta) * s(lambda), r * s(beta));
}

const equatorialToEcliptic = new THREE.Matrix4().makeRotationX(-OBLIQUITY);

/** Rotation matrix mapping body-fixed axes (x = prime meridian, z = north pole) to ecliptic. */
export function bodyOrientation(def: BodyDef, d: number, out: THREE.Matrix4): THREE.Matrix4 {
  const ra = def.pole.ra * DEG;
  const dec = def.pole.dec * DEG;
  const W = ((def.W0 + def.Wdot * d) % 360) * DEG;
  const p = new THREE.Vector3(Math.cos(dec) * Math.cos(ra), Math.cos(dec) * Math.sin(ra), Math.sin(dec));
  const n = new THREE.Vector3(Math.cos(ra + Math.PI / 2), Math.sin(ra + Math.PI / 2), 0);
  const pn = new THREE.Vector3().crossVectors(p, n);
  const xb = n.clone().multiplyScalar(Math.cos(W)).addScaledVector(pn, Math.sin(W));
  const yb = new THREE.Vector3().crossVectors(p, xb);
  out.makeBasis(xb, yb, p);
  return out.premultiply(equatorialToEcliptic);
}

/** Orientation of the body's equatorial plane without spin (for rings / moon orbits). */
export function bodyEquatorFrame(def: BodyDef, out: THREE.Matrix4): THREE.Matrix4 {
  return bodyOrientation({ ...def, W0: 0, Wdot: 0 }, 0, out);
}

export { EARTH_MOON_MASS_RATIO, DEG };
